import { useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { DocumentPreview } from "./DocumentPreview";

interface FullScreenDocumentPreviewProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  title?: string;
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

function isImageUrl(url: string): boolean {
  const ext = url.toLowerCase().split("?")[0].split(".").pop() ?? "";
  return IMAGE_EXTENSIONS.includes(ext);
}

export function FullScreenDocumentPreview({
  open,
  onClose,
  fileUrl,
  title = "Document Preview",
}: FullScreenDocumentPreviewProps) {
  const showImage = useMemo(() => isImageUrl(fileUrl), [fileUrl]);

  return (
    <SafeDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          width: "95vw",
          height: "95vh",
          maxWidth: "95vw",
          maxHeight: "95vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="span" noWrap>
          {title}
        </Typography>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {showImage ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
                backgroundColor: "#f5f5f5",
              }}
            >
              <img
                src={fileUrl}
                alt="Document preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <DocumentPreview fileUrl={fileUrl} />
          )}
        </Box>
      </DialogContent>
    </SafeDialog>
  );
}
